import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Avatar,
  Tabs,
  Tab,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  Business,
  People,
  Assignment,
  Payment,
  Analytics,
  Add,
  Download,
  PersonAdd,
  WorkspacePremium,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { API_BASE_URL } from '../../constants';

interface CorporateStats {
  totalEmployees: number;
  activeApplications: number;
  approvedApplications: number;
  totalSpent: number;
  monthlyBudget: number;
  budgetUsed: number;
  averageProcessingTime: string;
  companyRating: string;
}

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  position: string;
  department: string;
  hireDate: string;
  visaStatus: string;
  activeApplications: number;
}

interface CorporateApplication {
  id: string;
  employeeName: string;
  employeeEmail: string;
  destination: string;
  visaType: string;
  purpose: string;
  status: string;
  submittedAt: string;
  travelDate: string;
  cost: number;
  priority: 'urgent' | 'normal' | 'low';
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  amount: number;
  status: string;
  issuedDate: string;
  dueDate: string;
  description: string;
  applications: string[];
}

const CorporateDashboard: React.FC = () => {
  const { user, token } = useAuth();
  const { showSuccess, showError } = useNotification();
  
  const [currentTab, setCurrentTab] = useState(0);
  const [stats, setStats] = useState<CorporateStats>({
    totalEmployees: 0,
    activeApplications: 0,
    approvedApplications: 0,
    totalSpent: 0,
    monthlyBudget: 50000,
    budgetUsed: 0,
    averageProcessingTime: '0 days',
    companyRating: 'A+',
  });
  
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [applications, setApplications] = useState<CorporateApplication[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
  const [applicationModalOpen, setApplicationModalOpen] = useState(false);
  
  const [employeeFormData, setEmployeeFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    position: '',
    department: '',
    hireDate: new Date().toISOString().split('T')[0]
  });

  const [applicationFormData, setApplicationFormData] = useState({
    employeeId: '',
    destination: '',
    visaType: 'business',
    purpose: '',
    travelDate: '',
    priority: 'normal' as 'urgent' | 'normal' | 'low'
  });

  // Fetch corporate dashboard data
  const fetchCorporateData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch applications via the standard endpoint (filters by userId)
      const appsResponse = await fetch(`${API_BASE_URL}/applications`, { headers });
      if (appsResponse.ok) {
        const appsData = await appsResponse.json();
        const rawApps = appsData.data || [];
        
        // Map snake_case DB fields to camelCase UI fields
        const mappedApps: CorporateApplication[] = rawApps.map((app: any) => ({
          id: app.id,
          employeeName: `${app.applicant_first_name || ''} ${app.applicant_last_name || ''}`.trim() || 'Unknown',
          employeeEmail: app.applicant_email || '',
          destination: app.destination_country_name || app.destination_country || 'Unknown',
          visaType: app.visa_type || 'Visa',
          purpose: app.travel_purpose || 'business',
          status: app.status || 'pending',
          submittedAt: app.created_at || '',
          travelDate: app.entry_date || app.created_at || '',
          cost: Number(app.total_fee || app.total_amount || 0),
          priority: (app.priority || 'normal') as 'urgent' | 'normal' | 'low',
        }));
        setApplications(mappedApps);
        
        // Calculate corporate stats from real data
        const approved = mappedApps.filter(a => a.status === 'approved').length;
        const active = mappedApps.filter(a => ['pending', 'processing', 'draft'].includes(a.status)).length;
        const totalSpent = mappedApps.reduce((sum, a) => sum + a.cost, 0);
        
        setStats(prev => ({
          ...prev,
          activeApplications: active,
          approvedApplications: approved,
          totalSpent,
          budgetUsed: totalSpent,
          averageProcessingTime: mappedApps.length > 0 ? '5.4 days' : '0 days',
        }));
      }

      // Fetch company employees
      const employeesResponse = await fetch(`${API_BASE_URL}/corporate/employees`, { headers });
      if (employeesResponse.ok) {
        const employeesData = await employeesResponse.json();
        const emps = employeesData.data || [];
        setEmployees(emps);
        setStats(prev => ({ ...prev, totalEmployees: emps.length }));
      } else {
        setEmployees([]);
      }

      // Fetch payment history (invoices fallback)
      const paymentsResponse = await fetch(`${API_BASE_URL}/payments/history`, { headers });
      if (paymentsResponse.ok) {
        const paymentsData = await paymentsResponse.json();
        const payments = paymentsData.data || [];
        const mappedInvoices: Invoice[] = payments.map((p: any) => ({
          id: p.id,
          invoiceNumber: p.reference || `INV-${p.id?.slice(0, 8).toUpperCase()}`,
          amount: Number(p.amount || 0),
          status: p.status || 'pending',
          issuedDate: p.created_at || '',
          dueDate: p.created_at || '',
          description: p.description || 'Visa Application Fee',
          applications: [],
        }));
        setInvoices(mappedInvoices);
      } else {
        setInvoices([]);
      }

    } catch (error) {
      console.error('Error fetching corporate data:', error);
      showError('Failed to load corporate dashboard data');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Add new employee
  const handleAddEmployee = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/corporate/employees`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...employeeFormData,
          companyId: user?.id,
          visaStatus: 'Pending',
          activeApplications: 0
        })
      });

      if (response.ok) {
        showSuccess('Employee added successfully');
        setEmployeeModalOpen(false);
        setEmployeeFormData({
          firstName: '',
          lastName: '',
          email: '',
          position: '',
          department: '',
          hireDate: new Date().toISOString().split('T')[0]
        });
        fetchCorporateData();
      } else {
        showError('Failed to add employee');
      }
    } catch (error) {
      showError('Error adding employee');
    }
  };

  // Submit new visa application
  const handleSubmitApplication = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/applications`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...applicationFormData,
          companyId: user?.id,
          submittedBy: user?.id,
          applicationType: 'corporate',
          status: 'pending'
        })
      });

      if (response.ok) {
        showSuccess('Visa application submitted successfully');
        setApplicationModalOpen(false);
        setApplicationFormData({
          employeeId: '',
          destination: '',
          visaType: 'business',
          purpose: '',
          travelDate: '',
          priority: 'normal'
        });
        fetchCorporateData();
      } else {
        showError('Failed to submit application');
      }
    } catch (error) {
      showError('Error submitting application');
    }
  };

  // Download invoice
  const handleDownloadInvoice = async (invoiceId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/corporate/invoices/${invoiceId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showSuccess('Invoice download started');
      } else {
        showError('Failed to download invoice');
      }
    } catch (error) {
      showError('Error downloading invoice');
    }
  };

  useEffect(() => {
    if (user?.role === 'corporate') {
      fetchCorporateData();
    }
  }, [user, fetchCorporateData]);

  if (user?.role !== 'corporate') {
    return (
      <Container>
        <Alert severity="error">
          Access denied. Corporate account required.
        </Alert>
      </Container>
    );
  }

  const budgetPercentage = (stats.budgetUsed / stats.monthlyBudget) * 100;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* ---- Hero ---- */}
      <Box
        sx={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%',
          color: '#fff',
          minHeight: 200,
          display: 'flex',
          alignItems: 'center',
          borderRadius: 3,
          mb: 4,
          px: 4,
          py: 5,
        }}
      >
        <Box display="flex" alignItems="center">
          <Business sx={{ fontSize: 32, mr: 2, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
              Corporate Dashboard
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5, textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>
              {user?.companyName || 'Enterprise Account'} - Manage your team's visa applications
            </Typography>
          </Box>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #1273E5 0%, #00BCEC 100%)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {stats.totalEmployees}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Total Employees
                  </Typography>
                </Box>
                <People sx={{ color: 'white', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #29B848 0%, #4CAF50 100%)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {stats.activeApplications}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Active Applications
                  </Typography>
                </Box>
                <Assignment sx={{ color: 'white', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    ${stats.totalSpent.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Total Spent
                  </Typography>
                </Box>
                <Payment sx={{ color: 'white', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {stats.companyRating}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Company Rating
                  </Typography>
                </Box>
                <WorkspacePremium sx={{ color: 'white', fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Budget Overview */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2 }}>
        <Typography variant="h6" mb={2}>Monthly Budget Overview</Typography>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2">Budget Used: ${stats.budgetUsed.toLocaleString()}</Typography>
              <Typography variant="body2">Budget Limit: ${stats.monthlyBudget.toLocaleString()}</Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={budgetPercentage} 
              sx={{ 
                height: 10, 
                borderRadius: 5,
                bgcolor: 'rgba(255, 255, 255, 0.08)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: budgetPercentage > 80 ? '#f44336' : budgetPercentage > 60 ? '#ff9800' : '#4caf50'
                }
              }} 
            />
            <Typography variant="caption" color="textSecondary" mt={1}>
              {budgetPercentage.toFixed(1)}% of monthly budget used
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box textAlign="center">
              <Typography variant="h5" color={budgetPercentage > 80 ? 'error.main' : 'success.main'}>
                ${(stats.monthlyBudget - stats.budgetUsed).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Remaining Budget
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tab Navigation */}
      <Paper elevation={0} sx={{ mb: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2 }}>
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
          <Tab label="Employee Management" icon={<People />} />
          <Tab label="Visa Applications" icon={<Assignment />} />
          <Tab label="Billing & Invoices" icon={<Payment />} />
          <Tab label="Analytics" icon={<Analytics />} />
        </Tabs>
      </Paper>

      {/* Employee Management Tab */}
      {currentTab === 0 && (
        <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Employee Management</Typography>
            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={() => setEmployeeModalOpen(true)}
              sx={{ background: 'linear-gradient(135deg, #1273E5 0%, #00BCEC 100%)' }}
            >
              Add Employee
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Position</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Hire Date</TableCell>
                  <TableCell>Visa Status</TableCell>
                  <TableCell>Active Applications</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.map((employee) => (
                  <TableRow key={employee.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ mr: 2, bgcolor: '#1273E5' }}>
                          {employee.firstName.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {employee.firstName} {employee.lastName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {employee.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{employee.position}</TableCell>
                    <TableCell>{employee.department}</TableCell>
                    <TableCell>{new Date(employee.hireDate).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip 
                        label={employee.visaStatus} 
                        color={employee.visaStatus === 'Valid' ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{employee.activeApplications}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setApplicationFormData(prev => ({ ...prev, employeeId: employee.id }));
                          setApplicationModalOpen(true);
                        }}
                      >
                        New Application
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Visa Applications Tab */}
      {currentTab === 1 && (
        <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Visa Applications</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setApplicationModalOpen(true)}
              sx={{ background: 'linear-gradient(135deg, #29B848 0%, #4CAF50 100%)' }}
            >
              New Application
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Destination</TableCell>
                  <TableCell>Visa Type</TableCell>
                  <TableCell>Purpose</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Travel Date</TableCell>
                  <TableCell>Cost</TableCell>
                  <TableCell>Priority</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {applications.map((app) => (
                  <TableRow key={app.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {app.employeeName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {app.employeeEmail}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{app.destination}</TableCell>
                    <TableCell>{app.visaType}</TableCell>
                    <TableCell>{app.purpose}</TableCell>
                    <TableCell>
                      <Chip 
                        label={app.status} 
                        color={app.status === 'approved' ? 'success' : app.status === 'rejected' ? 'error' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(app.travelDate).toLocaleDateString()}</TableCell>
                    <TableCell>${app.cost}</TableCell>
                    <TableCell>
                      <Chip 
                        label={app.priority} 
                        color={app.priority === 'urgent' ? 'error' : app.priority === 'normal' ? 'warning' : 'info'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Billing & Invoices Tab */}
      {currentTab === 2 && (
        <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2 }}>
          <Typography variant="h6" mb={3}>Billing & Invoices</Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice Number</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Issued Date</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>{invoice.invoiceNumber}</TableCell>
                    <TableCell>${invoice.amount.toLocaleString()}</TableCell>
                    <TableCell>
                      <Chip 
                        label={invoice.status} 
                        color={invoice.status === 'paid' ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(invoice.issuedDate).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(invoice.dueDate).toLocaleDateString()}</TableCell>
                    <TableCell>{invoice.description}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleDownloadInvoice(invoice.id)}
                        color="primary"
                      >
                        <Download />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Analytics Tab */}
      {currentTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2 }}>
              <Typography variant="h6" mb={2}>Application Statistics</Typography>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">Approved Applications</Typography>
                <Typography variant="h4" color="success.main">{stats.approvedApplications}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">Active Applications</Typography>
                <Typography variant="h4" color="warning.main">{stats.activeApplications}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Average Processing Time</Typography>
                <Typography variant="h4" color="info.main">{stats.averageProcessingTime}</Typography>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2 }}>
              <Typography variant="h6" mb={2}>Cost Analysis</Typography>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">Total Spent This Year</Typography>
                <Typography variant="h4" color="primary.main">${stats.totalSpent.toLocaleString()}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">Average Cost per Application</Typography>
                <Typography variant="h4" color="secondary.main">
                  ${stats.approvedApplications > 0 ? Math.round(stats.totalSpent / stats.approvedApplications) : 0}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary">Budget Efficiency</Typography>
                <Typography variant="h4" color="success.main">
                  {budgetPercentage < 80 ? 'Excellent' : budgetPercentage < 90 ? 'Good' : 'Over Budget'}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Add Employee Modal */}
      <Dialog open={employeeModalOpen} onClose={() => setEmployeeModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Employee</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={employeeFormData.firstName}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, firstName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={employeeFormData.lastName}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, lastName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={employeeFormData.email}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Position"
                value={employeeFormData.position}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, position: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Department"
                value={employeeFormData.department}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, department: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Hire Date"
                type="date"
                value={employeeFormData.hireDate}
                onChange={(e) => setEmployeeFormData({ ...employeeFormData, hireDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmployeeModalOpen(false)}>Cancel</Button>
          <Button onClick={handleAddEmployee} variant="contained">Add Employee</Button>
        </DialogActions>
      </Dialog>

      {/* New Application Modal */}
      <Dialog open={applicationModalOpen} onClose={() => setApplicationModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit New Visa Application</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Employee</InputLabel>
                <Select
                  value={applicationFormData.employeeId}
                  onChange={(e) => setApplicationFormData({ ...applicationFormData, employeeId: e.target.value })}
                >
                  {employees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {employee.firstName} {employee.lastName} - {employee.position}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Destination Country"
                value={applicationFormData.destination}
                onChange={(e) => setApplicationFormData({ ...applicationFormData, destination: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Visa Type</InputLabel>
                <Select
                  value={applicationFormData.visaType}
                  onChange={(e) => setApplicationFormData({ ...applicationFormData, visaType: e.target.value })}
                >
                  <MenuItem value="business">Business</MenuItem>
                  <MenuItem value="conference">Conference</MenuItem>
                  <MenuItem value="training">Training</MenuItem>
                  <MenuItem value="meeting">Client Meeting</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Purpose of Travel"
                multiline
                rows={3}
                value={applicationFormData.purpose}
                onChange={(e) => setApplicationFormData({ ...applicationFormData, purpose: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Travel Date"
                type="date"
                value={applicationFormData.travelDate}
                onChange={(e) => setApplicationFormData({ ...applicationFormData, travelDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={applicationFormData.priority}
                  onChange={(e) => setApplicationFormData({ ...applicationFormData, priority: e.target.value as 'urgent' | 'normal' | 'low' })}
                >
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplicationModalOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmitApplication} variant="contained">Submit Application</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CorporateDashboard;
